import React, { useState, useCallback, useMemo, memo } from 'react';
import { Box, Typography, Icon, ButtonBase, Tooltip } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { useNewAppointment } from '../../context';
import { useSchedule } from '../context';

import Loading from './Loading';
import useStyles from './styles';
import SlotProfessionalsModal from './SlotProfessionalsModal';
import SlotDetailsModal from './SlotDetailsModal';

const Slot = ({
  children,
  label,
  sticked,
  className,
  loading,
  slotInfos,
  columnDate,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { setNewAppointmentInfos } = useNewAppointment();
  const { filterProfessional } = useSchedule();
  const [openProfessionalsList, setOpenProfessionalsList] = useState(false);
  const [openDetailsAppointment, setOpenDetailsAppointment] = useState(false);

  const slotLabel = useMemo(() => {
    if (slotInfos) {
      const { status } = slotInfos;
      switch (status) {
        case 'AVALIABLE':
        case 'CANCELED':
          return 'Disponível';
        case 'SCHEDULED':
          return 'Agendado';
        case 'FULLROOM':
          return 'Indisponível';
        case 'BEFORE_NOW':
          return 'Indisponível';
        default:
          return '';
      }
    }
    return '';
  }, [slotInfos]);

  const slotVacancies = useMemo(() => {
    const vacanciesInfos = {
      filled: 0,
      total: 0,
      msg: '',
    };
    if (slotInfos?.professionals?.length) {
      if (filterProfessional) {
        const professionalListIndex = slotInfos.professionals.findIndex(
          item => item.professionalId === filterProfessional,
        );
        if (professionalListIndex === -1) {
          return vacanciesInfos;
        }

        const { vacanciesFilled, vacanciesTotal } = slotInfos.professionals[
          professionalListIndex
        ];
        vacanciesInfos.filled = vacanciesFilled;
        vacanciesInfos.total = vacanciesTotal;
        vacanciesInfos.msg = `${vacanciesFilled}/${vacanciesTotal}`;
        return vacanciesInfos;
      }
      const [vacanciesFilled, vacanciesTotal] = slotInfos.professionals.reduce(
        (accumulator, professional) => [
          accumulator[0] + professional.vacanciesFilled,
          accumulator[1] + professional.vacanciesTotal,
        ],
        [0, 0],
      );
      vacanciesInfos.filled = vacanciesFilled;
      vacanciesInfos.total = vacanciesTotal;
      vacanciesInfos.msg = `${vacanciesFilled}/${vacanciesTotal}`;
    }
    return vacanciesInfos;
  }, [slotInfos, filterProfessional]);

  const tooltipVacancies = useMemo(() => {
    const { filled, total } = slotVacancies;
    if (!filled && !total) return '';

    const available = total - filled;
    if (available < 1) {
      return `Não restam vagas disponíveis para este horário.`;
    }
    if (available === 1) {
      return `Resta 1 vaga disponível para este horário.`;
    }
    return `Restam ${available} vagas disponíveis para este horário.`;
  }, [slotVacancies]);

  const handleSelectSlot = useCallback(
    (professionalId, meetingId) => {
      if (!slotInfos) return;
      setNewAppointmentInfos(oldNewAppointmentInfos => ({
        ...oldNewAppointmentInfos,
        timeTableId: slotInfos.timeTableId,
        columnDate,
        professionalId,
        meetingId,
      }));
      history.push('/appointments/new/informations');
    },
    [setNewAppointmentInfos, history, slotInfos, columnDate],
  );

  const handleClickSlot = useCallback(() => {
    if (!slotInfos) return;
    if (slotInfos?.status === 'FULLROOM') return;
    if (slotInfos?.status === 'BEFORE_NOW') return;
    if (slotInfos?.status === 'SCHEDULED') {
      setOpenDetailsAppointment(true);
      return;
    }
    setOpenProfessionalsList(true);
  }, [slotInfos]);

  const handleDetailsAppointmentClose = useCallback(() => {
    setOpenDetailsAppointment(false);
  }, []);

  const handleProfessionalsListClose = useCallback(() => {
    setOpenProfessionalsList(false);
  }, []);

  return (
    <Box
      className={`
        ${classes.container}
        ${slotInfos?.status === 'SCHEDULED' ? 'scheduled' : ''}
        ${slotInfos?.status === 'EMPTY' ? 'empty' : ''}
        ${slotInfos?.status === 'FULLROOM' ? 'fullroom' : ''}
        ${slotInfos?.status === 'BEFORE_NOW' ? 'before-now' : ''}
        ${label ? 'label' : ''}
        ${sticked ? 'sticked' : ''}
        ${className || ''}
      `}
    >
      <Box className="slot-content">
        {loading && <Loading />}
        {!loading && !slotInfos && (
          <Typography variant="caption">{children}</Typography>
        )}
        {!loading && slotInfos && slotInfos.status !== 'EMPTY' && (
          <>
            <Tooltip
              title={
                slotInfos?.status === 'BEFORE_NOW'
                  ? 'Não é possível realizar um agendamento com horário de início anterior ao horário atual.'
                  : ''
              }
            >
              <ButtonBase
                className="slot-button-container"
                onClick={handleClickSlot}
              >
                <Icon className="slot-button-icon" fontSize="small">
                  {slotInfos?.status === 'SCHEDULED' ? 'check_circle' : ''}
                  {slotInfos?.status === 'AVALIABLE' ? 'schedule' : ''}
                  {slotInfos?.status === 'CANCELED' ? 'schedule' : ''}
                  {slotInfos?.status === 'FULLROOM' ? 'highlight_off' : ''}
                  {slotInfos?.status === 'BEFORE_NOW' ? 'highlight_off' : ''}
                </Icon>
                <Typography className="slot-button-text" variant="caption">
                  {slotLabel}
                </Typography>
                {slotInfos?.status !== 'SCHEDULED' && !!slotVacancies.msg && (
                  <Tooltip
                    title={
                      slotInfos?.status !== 'BEFORE_NOW' ? tooltipVacancies : ''
                    }
                  >
                    <Typography
                      className="slot-button-vacancies"
                      variant="caption"
                    >
                      {slotVacancies.msg}
                    </Typography>
                  </Tooltip>
                )}
              </ButtonBase>
            </Tooltip>
            {openDetailsAppointment && (
              <SlotDetailsModal
                historyId={slotInfos.historyId}
                onExited={handleDetailsAppointmentClose}
              />
            )}
            {openProfessionalsList && (
              <SlotProfessionalsModal
                date={columnDate}
                timeTableId={slotInfos.timeTableId}
                historyId={slotInfos.historyId}
                professionalsSlotInfos={slotInfos.professionals}
                onExited={handleProfessionalsListClose}
                onSelect={handleSelectSlot}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default memo(Slot);
